import { UnitOfDistance } from '@treadinc/horizon-api-spec';
import parsePhoneNumber from 'libphonenumber-js';
import { isNil } from 'lodash-es';
import { matchIsValidTel } from 'mui-tel-input';

import { Nullable } from '~types/Nullable';

// Function to compare objects by values (including nested objects)
const compareValues = (obj1: unknown, obj2: unknown, excludedProps: string[] = []) => {
  // https://lodash.com/docs/4.17.15#isEqual doing the same thing

  if (obj1 === obj2) {
    return true; // If both objects are the same reference, they are equal
  }
  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false; // If one of them is not an object (including null), they are not equal
  }

  // Check if both objects belong to the same class
  if (obj1.constructor !== obj2.constructor) {
    return false;
  }

  // Get the property names of both objects
  const props1 = Object.getOwnPropertyNames(obj1);
  const props2 = Object.getOwnPropertyNames(obj2);
  if (props1.length !== props2.length) {
    return false; // If they have a different number of properties, they are not equal
  }

  const record1 = obj1 as Record<string, unknown>;
  const record2 = obj2 as Record<string, unknown>;

  // Compare each property recursively
  for (const prop of props1) {
    if (excludedProps.includes(prop as string)) {
      continue; // Skip the comparison for excluded properties
    }

    const value1 = record1[prop];
    const value2 = record2[prop];

    if (!compareValues(value1, value2, excludedProps)) {
      return false;
    }
  }

  return true; // If all checks pass, the objects are equal
};

const splitStringBySentenceChange = (str: string): string => {
  return str ? str.replace(/([a-z])([A-Z])/g, '$1 $2') : '';
};

const snakeCaseToHuman = (str: string): string => {
  if (!str) return '';

  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const humanToSnakeCase = (str: string): string => {
  if (!str) return '';

  return str
    .split(' ')
    .map((word) => word.toLowerCase())
    .join('_');
};

const matchIsValidTelOrTestTel = (value: string) => {
  const valueWithoutSpaces = value.replace(/\s+/g, '');

  if (valueWithoutSpaces.startsWith('+11') && !valueWithoutSpaces.startsWith('+111')) {
    return false;
  }

  const allowedAreaCodes = ['111', '000'];
  const areaCode = valueWithoutSpaces.replace('+1', '').slice(0, 3);
  if (allowedAreaCodes.includes(areaCode)) {
    return valueWithoutSpaces.length === 12;
  }

  return matchIsValidTel(value);
};

/**
 * Formats a phone number for display in read-only contexts.
 * US/CA numbers: +1 (314) 537-4728
 * Other countries: International format from libphonenumber-js
 */
const formatPhoneForDisplay = (phone: string | null | undefined): string => {
  if (!phone) return '';

  try {
    const parsed = parsePhoneNumber(phone);
    if (!parsed) return phone;

    if (parsed.country === 'US' || parsed.country === 'CA') {
      const national = parsed.nationalNumber;
      if (national.length === 10) {
        return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
      }
    }

    return parsed.formatInternational();
  } catch {
    return phone;
  }
};

/**
 * Formats a phone number for submitting it to the API.
 * Removes all characters except for "+" and numbers.
 */
function formatPhoneForSubmit(phone: string | null | undefined) {
  return phone?.replace(/\s/g, '').replace(/[^+\d]/g, '') || undefined;
}

const PHONE_DIGITS_AND_SEPARATORS_ONLY = /^\+?[\d\s().-]+$/;
const NANP_COUNTRY_CALLING_CODE = '1';

/**
 * Converts a phone number typed or pasted into a spreadsheet to E.164, assuming a US
 * number when there is no country code. Returns undefined for blank input, text,
 * extensions, and numbers that are not the right length for their country.
 * Unlike `formatPhoneForSubmit`, which only strips characters, this validates the number.
 */
const toE164PhoneNumber = (phone: string | null | undefined): string | undefined => {
  const text = phone?.trim();
  if (!text || !PHONE_DIGITS_AND_SEPARATORS_ONLY.test(text)) return undefined;

  const parsed = parsePhoneNumber(text, 'US');
  if (!parsed?.isPossible()) return undefined;

  // NANP area codes never start with 0 or 1, but the library only checks length
  const hasImpossibleNanpAreaCode =
    parsed.countryCallingCode === NANP_COUNTRY_CALLING_CODE &&
    /^[01]/.test(parsed.nationalNumber);

  return hasImpossibleNanpAreaCode ? undefined : parsed.number;
};

const hexToRgbaArray = (hex: string, alpha: number): [number, number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return [r, g, b, alpha];
};

const hexToRgba = (hex: string, alpha: number) => {
  return `rgba(${hexToRgbaArray(hex, alpha).join(',')})`;
};

const nullableStringOrNumberIsValidNumber = (input: string | number | null) => {
  if (isNil(input) || String(input).length === 0) {
    return false;
  }

  const inputAsNumber = parseFloat(String(input));

  return Number.isFinite(inputAsNumber);
};

const parseFiniteNumber = (input: string | number | null | undefined): number | null => {
  if (isNil(input) || String(input).trim() === '') {
    return null;
  }
  const parsed = Number(input);
  return Number.isFinite(parsed) ? parsed : null;
};

// A cleared number input holds '' while derived values arrive as strings or numbers, so
// telling a real change from a re-write of the same value takes the same normalization
// the API payload applies.
const numericFormValuesMatch = (
  formValue: string | number | null | undefined,
  candidateValue: string | number | null | undefined,
) => {
  return parseFiniteNumber(formValue) === parseFiniteNumber(candidateValue);
};

const convertMeters = (meters: number, to: UnitOfDistance) => {
  const divisor = to === UnitOfDistance.KILOMETER ? 1000 : 1000 * 1.6;

  return meters / divisor;
};

const convertToMeters = (distance: number, from: UnitOfDistance) => {
  const factor = from === UnitOfDistance.KILOMETER ? 1000 : 1000 * 1.6;
  return distance * factor;
};

const downloadFile = (
  fileName: string,
  fileUrl: string,
  shouldOpenInNewTab: boolean = false,
) => {
  const node = document.createElement('a');
  node.setAttribute('href', fileUrl);
  node.setAttribute('download', fileName);

  if (shouldOpenInNewTab) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  node.click();
  node.remove();
};

const fileIsImage = (fileMimeType: string) => {
  return fileMimeType.startsWith('image/');
};

/**
 * Returns an integer (in string format) if the value is an integer, otherwise returns a decimal rounded to 2 decimal places
 * @param value - The value to convert
 * @returns A string containing an integer or a decimal rounded to 2 decimal places
 */
const numberToIntegerOrDecimal = (value: number) => {
  if (Number.isInteger(value)) {
    return parseInt(value.toString(), 10).toString();
  }

  return value.toFixed(2);
};

const getStringOrNumberFieldPositiveNumberValue = (value: string | number | null) => {
  const isValidNumber = nullableStringOrNumberIsValidNumber(value);

  if (isValidNumber) {
    const number = Number(value);

    if (number >= 0) {
      return number;
    }
  }

  return null;
};

const formQuantityFieldRepresentsEmptyQuantity = (
  quantity: Nullable<string | number>,
) => {
  return isNil(quantity) || String(quantity).trim() === '' || Number(quantity) === 0;
};

/**
 * Format a percentage value for display.
 * Min 1 decimal place, max 4, strip trailing zeros beyond the first decimal.
 * Negative values use accounting notation: -5.25 → "(5.25%)"
 */
const formatPercentage = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0.0%';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.0%';

  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const fixed = absNum.toFixed(4);
  // Remove trailing zeros, but keep at least 1 decimal place
  const trimmed = fixed.replace(/(\.\d*?)0+$/, '$1');
  const result = trimmed.endsWith('.') ? trimmed + '0' : trimmed;

  return isNegative ? `(${result}%)` : `${result}%`;
};

export {
  compareValues,
  convertMeters,
  convertToMeters,
  downloadFile,
  fileIsImage,
  formatPercentage,
  formatPhoneForDisplay,
  formatPhoneForSubmit,
  formQuantityFieldRepresentsEmptyQuantity,
  getStringOrNumberFieldPositiveNumberValue,
  hexToRgba,
  hexToRgbaArray,
  humanToSnakeCase,
  matchIsValidTelOrTestTel,
  nullableStringOrNumberIsValidNumber,
  numberToIntegerOrDecimal,
  numericFormValuesMatch,
  parseFiniteNumber,
  snakeCaseToHuman,
  splitStringBySentenceChange,
  toE164PhoneNumber,
};
